import { useEffect, useState } from "react";

import {
  getCrimeCodes,
  getSubmissionCrimeCodes,
  postSubmissionCrimeCode,
  deleteSubmissionCrimeCode,
  CrimeCodeModel,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";

import CrimeCodesMultiSelect from "./CrimeCodesMultiSelect";

interface CrimeCodeSelectorProps {
  orgId: string;
  submissionId: string;
  surveyClass: string;
  enabled: boolean;
  readOnly?: boolean;
  required?: boolean;
  onCodesChange?: (count: number) => void;
}

const CrimeCodeSelector: React.FC<CrimeCodeSelectorProps> = ({
  orgId,
  submissionId,
  surveyClass,
  enabled,
  readOnly = false,
  required = false,
  onCodesChange,
}) => {
  const [availableCodes, setAvailableCodes] = useState<CrimeCodeModel[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<CrimeCodeModel[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !orgId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const [codesResponse, submissionCodesResponse] = await Promise.all([
          getCrimeCodes({
            path: { org_id: orgId },
          }),
          getSubmissionCrimeCodes({
            path: { org_id: orgId, submission_id: submissionId },
          }),
        ]);

        const allCodes = codesResponse.data || [];
        // Filter codes visible on this survey class
        const visibleCodes = allCodes.filter((code) => {
          if (code.is_archived) return false;

          const classes = code.visible_on_survey_classes;
          if (!classes) return true; // null means visible on all

          // Handle both array and object formats
          if (Array.isArray(classes)) {
            return classes.length === 0 || classes.includes(surveyClass);
          }

          return true;
        });

        setAvailableCodes(visibleCodes);

        const submissionCodeIds = (submissionCodesResponse.data || []).map(
          (c) => c.id,
        );
        const selected = submissionCodeIds
          .map((id) => visibleCodes.find((code) => code.id === id))
          .filter((code): code is CrimeCodeModel => code !== undefined);
        setSelectedCodes(selected);
        onCodesChange?.(selected.length);
      } catch (error) {
        console.error("Error fetching crime codes:", error);
        toast.error("Failed to load crime codes");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orgId, submissionId, surveyClass, enabled]);

  const handleChange = async (
    _event: React.SyntheticEvent,
    newValue: CrimeCodeModel[],
  ) => {
    const added = newValue.filter(
      (code) => !selectedCodes.find((c) => c.id === code.id),
    );
    const removed = selectedCodes.filter(
      (code) => !newValue.find((c) => c.id === code.id),
    );

    try {
      // Add new codes
      for (const code of added) {
        await postSubmissionCrimeCode({
          path: { org_id: orgId, submission_id: submissionId },
          body: { crime_code_id: code.id },
        });
      }

      // Remove codes
      for (const code of removed) {
        await deleteSubmissionCrimeCode({
          path: {
            org_id: orgId,
            submission_id: submissionId,
            crime_code_id: code.id,
          },
        });
      }

      setSelectedCodes(newValue);
      onCodesChange?.(newValue.length);
      toast.success("Crime codes updated");
    } catch (error) {
      console.error("Error updating crime codes:", error);
      toast.error("Failed to update crime codes");
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <CrimeCodesMultiSelect
      options={availableCodes}
      value={selectedCodes}
      onChange={handleChange}
      loading={loading}
      readOnly={readOnly}
      required={required}
    />
  );
};

export default CrimeCodeSelector;
